import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { engagementsAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import { ArrowLeft, RefreshCw, Briefcase, Save } from 'lucide-react';
import '../Admin/AdminDashboard.css';

const STATUSES = ['pipeline', 'proposal', 'active', 'delivered', 'invoiced', 'closed', 'lost'];
const TYPES = ['engineering_services', 'managed_part_supply', 'framework', 'subscription', 'other'];

const AdminEngagementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await engagementsAPI.getOne(id);
      const row = res.data.item;
      setItem(row);
      setForm({
        engagementId: row.engagementId || '',
        title: row.title || '',
        type: row.type || 'engineering_services',
        status: row.status || 'pipeline',
        conflictCheckId: row.conflictCheckId || '',
        valueEur: row.valueEur != null ? String(row.valueEur) : '',
        notes: row.notes || '',
      });
    } catch {
      toast.error('Failed to load engagement');
      navigate('/admin/engagements');
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await engagementsAPI.update(id, {
        ...form,
        valueEur: form.valueEur === '' ? undefined : Number(form.valueEur),
      });
      toast.success('Saved');
      load();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <main className="admin-content" style={{ color: '#71717a', padding: 40 }}>Loading…</main>
      </div>
    );
  }

  const screen = item?.screenId && typeof item.screenId === 'object' ? item.screenId : null;
  const customer = item?.customerId && typeof item.customerId === 'object' ? item.customerId : null;

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content">
        <div className="content-header">
          <div>
            <button type="button" onClick={() => navigate('/admin/engagements')} style={{ background: 'transparent', border: 'none', color: '#a1a1aa', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8, padding: 0 }}>
              <ArrowLeft size={16} /> Back to engagements
            </button>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Briefcase size={28} color="#eab308" />
              {form.title || form.engagementId || 'Engagement'}
            </h1>
            <p style={{ color: '#a1a1aa' }}>Detail shell — edit fields and link COI conflict check before commercial work proceeds.</p>
          </div>
          <div className="admin-header-right">
            <button type="button" onClick={handleSave} disabled={saving} style={btn('#eab308')}><Save size={15} /> {saving ? 'Saving…' : 'Save'}</button>
            <button type="button" className="btn-secondary" onClick={load}><RefreshCw size={16} /></button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
          <div style={card}>
            <h3 style={{ marginTop: 0, color: '#fff' }}>Commercial</h3>
            <div style={grid}>
              <label style={field}><span style={muted}>Engagement ID</span>
                <input value={form.engagementId} onChange={(e) => setForm((p) => ({ ...p, engagementId: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>Title</span>
                <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>Type</span>
                <select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} style={input}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select></label>
              <label style={field}><span style={muted}>Status</span>
                <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} style={input}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select></label>
              <label style={field}><span style={muted}>Value (€)</span>
                <input type="number" value={form.valueEur} onChange={(e) => setForm((p) => ({ ...p, valueEur: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>COI conflict check ID</span>
                <input value={form.conflictCheckId} onChange={(e) => setForm((p) => ({ ...p, conflictCheckId: e.target.value }))} style={input} /></label>
              <label style={{ ...field, gridColumn: '1 / -1' }}><span style={muted}>Notes</span>
                <textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={4} style={input} /></label>
            </div>
          </div>

          <div style={card}>
            <h3 style={{ marginTop: 0, color: '#fff' }}>Links</h3>
            <div style={{ fontSize: 13, color: '#a1a1aa', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div style={muted}>Screen</div>
                <div style={{ color: '#fff' }}>{screen ? `${screen.screenId || screen._id} · ${screen.customerName || ''} (${screen.status || ''})` : (item?.screenId || '—')}</div>
              </div>
              <div>
                <div style={muted}>Customer</div>
                <div style={{ color: '#fff' }}>{customer ? `${customer.name || ''} · ${customer.email || ''}` : (item?.customerId || '—')}</div>
              </div>
              <div>
                <div style={muted}>Created</div>
                <div style={{ color: '#fff' }}>{item?.createdAt ? new Date(item.createdAt).toLocaleString('en-GB') : '—'}</div>
              </div>
              <div>
                <div style={muted}>Updated</div>
                <div style={{ color: '#fff' }}>{item?.updatedAt ? new Date(item.updatedAt).toLocaleString('en-GB') : '—'}</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const btn = (c) => ({ background: c, border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 });
const card = { background: '#18181b', borderRadius: 12, border: '1px solid #27272a', padding: 24 };
const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const field = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 };
const muted = { color: '#71717a', fontSize: 12 };
const input = { background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 };

export default AdminEngagementDetail;
